import { MutableRefObject, ReactNode } from "react";
import {
  GetViewerForHostedResultQuery,
  UpdateCustomerHostedResultMutation,
  UpdateCustomerDetailsHostedResultMutation,
  CreateCustomerHostedResultMutation,
} from "../generated";
import { FetchResult, ApolloClient } from "@apollo/client/core";
import type { EducationLevel } from "@portal/app/_common/types/customerDetail";
import type { QuestionDescriptionProp } from "../../FormFieldRenderer";
import type { PostLeadRedirectMode } from "@portal/app/_server/services/formFunnel/payloadTypes";
import type { PhoneProgressStage } from "../../utils/funnelProgress";

/** Shared optional helper-text prop for hardcoded hosted steps. */
export interface HostedStepDescriptionProp {
  description?: QuestionDescriptionProp;
}

export interface CustomerData {
  nameFirst: string;
  nameLast: string;
  email: string;
  phone: string;
  educationLevel: EducationLevel;
  dob?: string;
  address?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip: string;
  };
}

/** Fields accumulated incrementally by customerDataRef during the hosted flow */
export type CustomerDataBuilder = {
  nameFirst: string;
  nameLast: string;
  email: string;
  phone: string;
  dob?: string;
  educationLevel?: EducationLevel;
  address?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip: string;
  };
};

/** Type guard that asserts all CustomerData required fields are present */
export function isCompleteCustomerData(data: CustomerDataBuilder): data is CustomerData {
  return (
    data.nameFirst.length > 0 &&
    data.nameLast.length > 0 &&
    data.email.length > 0 &&
    data.phone.length > 0 &&
    data.educationLevel !== undefined &&
    data.educationLevel.length > 0
  );
}

export type UpdateCustomerFn = (options: {
  variables: { data: Record<string, unknown> };
}) => Promise<FetchResult<UpdateCustomerHostedResultMutation>>;

export type UpdateCustomerDetailsFn = (options: {
  variables: { data: Record<string, unknown> };
}) => Promise<FetchResult<UpdateCustomerDetailsHostedResultMutation>>;

export type CreateCustomerFn = (options: {
  variables: { data: Record<string, unknown> };
}) => Promise<FetchResult<CreateCustomerHostedResultMutation>>;

export type RefetchViewerFn = () => Promise<
  ApolloClient.QueryResult<GetViewerForHostedResultQuery>
>;

export interface ContactStepProps extends HostedStepDescriptionProp {
  user: {
    nameFirst: string | null;
    nameLast: string | null;
    email: string | null;
  } | null;
  loggedIn: boolean;
  viewerData: GetViewerForHostedResultQuery | undefined;
  customerDataRef: MutableRefObject<CustomerDataBuilder>;
  updateCustomer: UpdateCustomerFn;
  createCustomer: CreateCustomerFn;
  refetchViewer: RefetchViewerFn;
  onNext: () => void;
  /** Optional back handler. When undefined, the Back action is not shown. */
  onBack?: () => void;
}

export interface PhoneStepProps extends HostedStepDescriptionProp {
  user: {
    phone: string | null;
    phoneVerified: boolean | null;
  } | null;
  customerDataRef: MutableRefObject<CustomerDataBuilder>;
  refetchViewer: RefetchViewerFn;
  onNext: () => void;
  /** Optional back handler. When undefined, the Back action is not shown. */
  onBack?: () => void;
  /** Reports the phone or code presentation currently visible to the student. */
  onProgressStageChange?: (stage: PhoneProgressStage) => void;
}

export interface AddressStepProps extends HostedStepDescriptionProp {
  viewerCustomer: GetViewerForHostedResultQuery["viewerCustomer"] | undefined;
  customerDataRef: MutableRefObject<CustomerDataBuilder>;
  updateCustomerDetails: UpdateCustomerDetailsFn;
  refetchViewer: RefetchViewerFn;
  onNext: () => void;
  /** Optional back handler. When undefined, the Back action is not shown. */
  onBack?: () => void;
}

export interface EligibilityStepProps {
  viewerCustomer: GetViewerForHostedResultQuery["viewerCustomer"] | undefined;
  customerDataRef: MutableRefObject<CustomerDataBuilder>;
  updateCustomerDetails: UpdateCustomerDetailsFn;
  refetchViewer: RefetchViewerFn;
  onNext: () => void;
  onBack?: () => void;
}

export interface ConfirmStepProps {
  program: {
    id: string;
    name: string;
    provider: {
      id: string;
      shortName: string;
      fullName: string;
      advertiserName: string;
      isTrustedFormEnabled: boolean;
    };
    customDisclaimer?: string | null;
  };
  user: {
    phone: string | null;
  } | null;
  customerDataRef: MutableRefObject<CustomerDataBuilder>;
  /** The form funnel ID, threaded as a prop to avoid requiring FormFunnelProvider context */
  funnelId: string;
  /** Per-tab form funnel attempt ID for linking the session row to an enrollment */
  formFunnelSessionId?: string;
  /** Collected form answers, threaded as a prop to avoid requiring FormFunnelProvider context */
  answers: Record<string, unknown>;
  /** Scoped fallback destination if the program becomes unavailable during submission. */
  searchUrl?: string;
  onComplete?: (redirectUrl?: string, firstName?: string) => void;
  /** Optional back handler. When undefined, the Back action is not shown. */
  onBack?: () => void;
  /** When true, skips the lead submission mutation */
  isPreview?: boolean;
  /** Custom CTA button text. Defaults to "Get Info" if not provided. */
  ctaButtonText?: string;
  /** Controls whether post-lead redirect happens immediately or after showing success page */
  postLeadRedirectMode?: PostLeadRedirectMode;
  /**
   * Pre-ping result payload from getFormFunnelMatches, threaded through to the
   * submit mutation so the server can persist persistedFields onto
   * enrollment_item.metadata.prePingResult.
   */
  prePingResult?: unknown;
  /** Displayed result-card rank selected by the student. */
  selectedResultRank?: number;
  /**
   * Reports whether the lead submission is in flight. The mutation survives
   * unmount, so ancestors that can navigate away from this step (e.g. the
   * multi-result chooser's first-step exit) must lock that navigation while true
   * or a second lead can be submitted concurrently.
   */
  onSubmittingChange?: (isSubmitting: boolean) => void;
  /** Successful lead/handoff gate, separate from completion-page rendering. */
  onValidatedSubmit?: (scenario: string) => void;
  /** Program summary rendered between the top Back link and confirmation heading. */
  programSummary?: ReactNode;
}
